import { z } from 'zod';
import {
  SubmittedCompanyDataResponseSchema,
  SubmittedCompanyDataSchema,
  requiredLegalFields,
  type SubmittedCompanyData,
} from './organization-onboarding.schemas';
import { LegalFormSchema } from './company.schemas';
import { PhoneNumberSchema } from '../common/phone-number.schema';

// Lifecycle status of a workspace's main-company onboarding (IMM-322). Unlike the
// partner organisation onboarding (which carries a magic-link + operator-review
// machine), the main company is filled in-app by an already-authenticated owner,
// so the lifecycle is just `in_progress` (opened on workspace creation) →
// `completed` (the owner submitted and the data was applied to the Company). No
// review states. lower_snake mirrors the member/organisation onboarding enums.
export const CompanyOnboardingStatusSchema = z.enum(['in_progress', 'completed']);

export type CompanyOnboardingStatus = z.infer<typeof CompanyOnboardingStatusSchema>;

// The company-onboarding wizard steps, in order (IMM-322). `legal` (Rechtliche
// Angaben) → `contact` (Kontakt). No consents step: the workspace owner is an
// authenticated member, not an external party agreeing to terms. Used as the
// per-step draft-save discriminator + the resume pointer the wizard hydrates.
export const CompanyOnboardingStepSchema = z.enum(['legal', 'contact']);

export type CompanyOnboardingStep = z.infer<typeof CompanyOnboardingStepSchema>;

export const CompanyOnboardingPathParamsSchema = z.object({ workspaceId: z.uuid() });

export type CompanyOnboardingPathParams = z.infer<typeof CompanyOnboardingPathParamsSchema>;

// The scoped onboarding state the owner sees over their session. `currentStep` is
// the non-regressing resume pointer (null before the first draft save);
// `companyData` is the draft snapshot so the wizard hydrates the form on reload
// (all-null when nothing has been drafted). Reuses the partner submission shape
// via its shared encode-safe response mirror.
export const CompanyOnboardingMeResponseSchema = z.object({
  status: CompanyOnboardingStatusSchema,
  currentStep: CompanyOnboardingStepSchema.nullable(),
  companyData: SubmittedCompanyDataResponseSchema,
});

export type CompanyOnboardingMeResponse = z.infer<typeof CompanyOnboardingMeResponseSchema>;

export const GetCompanyOnboardingResponseSchema = CompanyOnboardingMeResponseSchema;
export type GetCompanyOnboardingResponse = z.infer<typeof GetCompanyOnboardingResponseSchema>;

// One per-step draft save (IMM-322). The wizard sends which `step` it is saving
// plus the partial `companyData` gathered so far (all fields nullable). The draft
// snapshot REPLACES the parked company-data, and the resume pointer advances
// (never regresses).
export const SaveCompanyOnboardingDraftBodySchema = z.object({
  step: CompanyOnboardingStepSchema,
  companyData: SubmittedCompanyDataSchema,
});

export type SaveCompanyOnboardingDraftBody = z.infer<typeof SaveCompanyOnboardingDraftBodySchema>;

// Empty/whitespace string → null, so an absent value is treated as "not set"
// rather than failing a format check. Mirrors the draft schema's preprocessing.
const emptyToNull = (value: unknown): unknown => (typeof value === 'string' && value.trim() === '' ? null : value);

const HttpUrlSchema = z.url().refine((value) => /^https?:\/\//i.test(value), { message: 'must be an http(s) URL' });

const VatIdSchema = z
  .string()
  .trim()
  .regex(/^DE[0-9]{9}$/, { message: 'must be DE followed by 9 digits' });

const optionalText = (max: number) =>
  z.preprocess(emptyToNull, z.string().trim().min(1).max(max).nullable()).default(null);

// Strict address: same parts as the draft's `SubmittedAddressSchema` (all
// nullable as a whole), but format-validated like the canonical company write
// schema (`UpdateCompanyBodySchema`) — non-empty parts required together, postal
// code 5-digit.
const StrictSubmittedAddressSchema = z.object({
  street: z.string().trim().min(1).max(100),
  houseNumber: z.string().trim().min(1).max(20),
  postalCode: z
    .string()
    .trim()
    .regex(/^\d{5}$/),
  city: z.string().trim().min(1).max(80),
  country: z.string().trim().min(1).max(80),
  latitude: z.number().min(-90).max(90).nullable().default(null),
  longitude: z.number().min(-180).max(180).nullable().default(null),
});

// The strict company-data schema used ONLY on submit (IMM-322). Submit writes
// straight to `Company`, which relies on contract-boundary validation, so unlike
// the lenient draft schema (`SubmittedCompanyData`) this enforces the canonical
// company write formats (`UpdateCompanyBodySchema`) on any non-null value: email
// via `z.email()`, phone via the shared E.164 `PhoneNumberSchema`, postal code
// 5-digit, VAT `DE`+9 digits, website http(s) URL, strict address. Every field
// stays `T | null` so the inferred type remains assignable to the domain's
// `SubmittedCompanyData`. HRB (`commercialRegisterNumber`) and `website` stay
// OPTIONAL — required-ness is still enforced separately by `companySubmittableIssues`.
export const StrictSubmittedCompanyDataSchema = z.object({
  companyName: optionalText(120),
  legalForm: z.preprocess(emptyToNull, LegalFormSchema.nullable()).default(null),
  managingDirectors: optionalText(240),
  commercialRegisterCourt: optionalText(120),
  commercialRegisterNumber: optionalText(40),
  vatId: z.preprocess(emptyToNull, VatIdSchema.nullable()).default(null),
  supervisoryAuthority: optionalText(200),
  professionalIndemnityInsurance: optionalText(200),
  tradePermitAuthority: optionalText(200),
  contentResponsiblePerson: optionalText(200),
  website: z.preprocess(emptyToNull, HttpUrlSchema.nullable()).default(null),
  phone: z.preprocess(emptyToNull, PhoneNumberSchema.nullable()).default(null),
  email: z.preprocess(emptyToNull, z.email().nullable()).default(null),
  address: z.preprocess((value) => value ?? null, StrictSubmittedAddressSchema.nullable()).default(null),
});

// Compile-time guard: the strict submit payload must stay assignable to the
// domain's lenient `SubmittedCompanyData` (every field `T | null`), so the submit
// handler keeps consuming it unchanged.
type _StrictSubmittedCompanyDataAssignable =
  z.infer<typeof StrictSubmittedCompanyDataSchema> extends SubmittedCompanyData ? true : never;
const _strictSubmittedCompanyDataAssignable: _StrictSubmittedCompanyDataAssignable = true;
void _strictSubmittedCompanyDataAssignable;

export const SubmitCompanyOnboardingBodySchema = z.object({
  companyData: StrictSubmittedCompanyDataSchema,
});

export type SubmitCompanyOnboardingBody = z.infer<typeof SubmitCompanyOnboardingBodySchema>;

export const SubmitCompanyOnboardingResponseSchema = CompanyOnboardingMeResponseSchema;
export type SubmitCompanyOnboardingResponse = z.infer<typeof SubmitCompanyOnboardingResponseSchema>;

// One missing required item on a strict submission (IMM-322): which wizard step
// it lives on and which field is absent. The web-app routes the owner to `step`
// and field-marks `field`; the backend submit gate throws when any are present.
export type CompanySubmissionIssue = { readonly step: CompanyOnboardingStep; readonly field: string };

// The steps in wizard order; the strict gate reports issues in this order so the
// caller can route to the FIRST offending step.
export const COMPANY_ONBOARDING_STEP_ORDER: ReadonlyArray<CompanyOnboardingStep> = ['legal', 'contact'];

// Required address parts for the contact step (lat/lng stay optional).
const REQUIRED_ADDRESS_FIELDS = ['street', 'houseNumber', 'postalCode', 'city', 'country'] as const;

function isBlank(value: string | null | undefined): boolean {
  return value === null || value === undefined || value.trim() === '';
}

/**
 * The strict-submission gate (IMM-322). Pure: returns the missing required items
 * in step order (legal → contact), empty when the package is submittable. Shared
 * by the backend submit gate (throws when non-empty) and the web-app (routes to
 * the first offending step + field-marks). Required set:
 * - legal: every field in `requiredLegalFields(legalForm)` present (non-blank) —
 *   the Handelsregister (Gericht) only for Handelsregister forms (IMM-347);
 *   `website`, `commercialRegisterNumber`, `vatId`, and
 *   `professionalIndemnityInsurance` are optional.
 * - contact: `email`, `phone`, and an `address` with non-empty street, house
 *   number, postal code, city, and country (lat/lng optional).
 */
export function companySubmittableIssues(companyData: SubmittedCompanyData | null): CompanySubmissionIssue[] {
  const issues: CompanySubmissionIssue[] = [];

  for (const field of requiredLegalFields(companyData?.legalForm)) {
    if (isBlank(companyData?.[field] as string | null | undefined)) {
      issues.push({ step: 'legal', field });
    }
  }

  if (isBlank(companyData?.email)) {
    issues.push({ step: 'contact', field: 'email' });
  }

  if (isBlank(companyData?.phone)) {
    issues.push({ step: 'contact', field: 'phone' });
  }

  for (const part of REQUIRED_ADDRESS_FIELDS) {
    if (isBlank(companyData?.address?.[part])) {
      issues.push({ step: 'contact', field: `address.${part}` });
    }
  }

  return issues;
}
